import { CompoundMarker, Marker } from 'types';

export type Input = {
    markers: Marker[];
    markerLeftPadding?: number;
    markerRightPadding?: number;
    getTextWidth: (text: string) => number;
};

export function collapseMarkers(input: Input): CompoundMarker[] {
    // Given:
    // - a set of markers (each with a location and external data)
    // - a number of pixels of left-side padding for a marker
    // - a number of pixels of right-side padding for a marker
    // - a function that calculates the width in pixels of text
    //
    // Calculate an output set of markers where:
    // - input markers that are too close to eachother have been collapsed
    // - each marker has
    //    - a location
    //    - an ordered set of the input markers that were collapsed into it
    //
    const {
        markers = [],
        markerLeftPadding = 0,
        markerRightPadding = 0,
        getTextWidth,
    } = input;

    const sortByPosition = (a: Marker, b: Marker) => a.position - b.position;

    // Preconditions:
    // - all markers in a are to the left of all markers in b
    // - a and b each contain at least one marker
    // - a and b have the same marker type
    const collapseTwoMarkers = (a: CompoundMarker, b: CompoundMarker): CompoundMarker => {
        const allPositions = [
            ...a.markers.map(({ position }) => position),
            ...b.markers.map(({ position }) => position),
        ];
        // Calculate the average of all positions (rounded down)
        const position = Math.floor(allPositions.reduce((sum, p) => sum + p, 0) / allPositions.length);
        return {
            type: a.type,
            position,
            markers: [ ...a.markers, ...b.markers ],
        };
    };

    // Precondition: compoundMarkers are sorted by position ascending
    const findClosestPairFirstIndex = (compoundMarkers: CompoundMarker[]): number => {
        if (compoundMarkers.length < 2) {
            return -1;
        }

        let firstIndex = -1;
        let closestDistance = Number.MAX_VALUE;
        for (let i = 0; i < compoundMarkers.length - 1; i++) {
            const distance = compoundMarkers[i + 1].position - compoundMarkers[i].position;
            if (distance < closestDistance) {
                closestDistance = distance;
                firstIndex = i;
            }
        }
        return firstIndex;
    };

    // Helper function to calculate half of the width of a marker label
    // (Marker only contains text when two or more markers are collapsed)
    const getMarkerHalfTextWidth = ({ markers }: CompoundMarker): number =>
        markers.length < 2 ? 0 : Math.ceil(getTextWidth(markers.length.toString()) / 2)
    ;

    // Start with the output set of markers being the same as the input markers
    const compoundMarkers: CompoundMarker[] = markers.sort(sortByPosition).map(marker => ({
        type: marker.type,
        position: marker.position,
        markers: [ marker ],
    }));

    // Cannot simply use greedy algorithm because the text width from a newly
    // collapsed marker could push far enough to the left to overlap a previous
    // marker (which should then trigger it to collapse as well). So always
    // collapse the closest two markers until no two markers overlap.
    while (true) {
        // Find the closest pair of markers, bail out if we're done
        const i = findClosestPairFirstIndex(compoundMarkers);
        if (i < 0) {
            break;
        }

        const a = compoundMarkers[i];
        const aHalfTextWidth = getMarkerHalfTextWidth(a);

        const b = compoundMarkers[i + 1];
        const bHalfTextWidth = getMarkerHalfTextWidth(b);

        // Are marker a and marker b too close (or even overlapping)?
        const aRightExtent = a.position + aHalfTextWidth + markerRightPadding;
        const bLeftExtent = b.position - bHalfTextWidth - markerLeftPadding;
        if (aRightExtent >= bLeftExtent) {
            // Collapse marker a and b
            compoundMarkers.splice(i, 2, collapseTwoMarkers(a, b));
        }
        else {
            // Bail out because the two closest markers are not close enough to collapse.
            break;
        }
    }

    return compoundMarkers;
}



// WEBPACK FOOTER //
// ./src/workers/timeline/collapseMarkers.ts